import type { InvoiceStatusType } from '@/types';

/**
 * This file contains application-wide constants.
 */

/**
 * Number of items to display per page in paginated views.
 * @constant {number}
 */
export const ITEMS_PER_PAGE = 10;

/**
 * Page sizes a list lets the user choose from. milo-api caps `per_page` at 100
 * (PageSize::MAX), so a larger option would be rejected.
 * @constant {number[]}
 */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/**
 * Default locale for the application.
 * @constant {string}
 */
export const DEFAULT_LOCALE = 'nl';

/**
 * Default payment term for quotes in days.
 * @constant {number}
 */
export const DEFAULT_PAYMENT_TERM_QUOTES = 10;

/**
 * Default payment term for invoices in days.
 * @constant {number}
 */
export const DEFAULT_PAYMENT_TERM_INVOICES = 30;

/**
 * Default document-type filter for the invoices list: invoices and credit notes
 * are shown together. The API takes the comma-separated pair as a single `type`
 * filter value.
 * @constant {string}
 */
export const INVOICES_TYPE_FILTER = 'invoice,credit_note';

/**
 * Statuses of an invoice that is still open: the customer owes the amount, so an
 * expired (overdue) invoice counts too. Design-system decision 0013, mirroring
 * `OUTSTANDING_STATUSES` in milo-api's DashboardReportService.
 * @constant {readonly InvoiceStatusType[]}
 */
export const OPEN_INVOICE_STATUSES: readonly InvoiceStatusType[] = [
    'pending',
    'viewed',
    'expired',
    'reminded',
    'collection',
];

/**
 * Statuses of a quote that is still open: the customer can still respond. An
 * expired quote is no longer open (decision 0013).
 * @constant {readonly InvoiceStatusType[]}
 */
export const OPEN_QUOTE_STATUSES: readonly InvoiceStatusType[] = ['pending', 'viewed'];

/**
 * Re-export countries constants
 */
export { COUNTRIES_WITHOUT_ZIPCODE } from './countries';
